<template>
  <div v-if="activity"> 

    <div class="panel">
          <div class="w-full flex flex-wrap">
            <div class="panel">
              <form @submit.prevent="addPrelation" class="w-full">
                <div class="w-full flex">
                  <div class="w-1/3 pr-3">
                    <label for="type" class="w-full">{{$t('link_with_activity')}}</label>
                    <b-select ref="input"
                      v-model="item.type"
                      :placeholder="$t('link_with_activity')"
                      name="type"
                      class="w-full"
                      required
                      expanded
                    >
                      <option v-for="option in types" :value="option" :key="option">
                        {{ $t(option) }}
                      </option>
                    </b-select>
                  </div>

                  <div class="w-1/3 pr-3">
                    <label for="link_type" class="w-full">{{$t('link_type')}}</label>
                    <b-select ref="input"
                      v-model="item.link_type"
                      :placeholder="$t('link_type')"
                      name="link_type"
                      class="w-full"
                      required
                      expanded
                    >
                      <option v-for="option in linkTypes" :value="option" :key="option">
                        {{ option }}
                      </option>
                    </b-select>
                  </div>

                  <div class="w-1/3">
                    <label for="activity_id" class="w-full">{{ $t('select_activity') }}</label>
                    <b-autocomplete
                        v-model="activitySearch"
                        :data="filteredActivities"
                        :placeholder="$t('select_activity')"
                        :custom-formatter="optionLabel"
                        icon="search"
                        icon-pack="fas"
                        open-on-focus
                        clearable
                        keep-first
                        max-height="350px"
                        @select="onActivitySelect"
                        @input="text => !text && onActivitySelect(null)"
                    >
                      <template v-slot="props">
                        <span v-if="props.option.planning_code" class="font-bold mr-2">{{ props.option.planning_code }}</span>{{ props.option.name }}
                      </template>
                    </b-autocomplete>
                  </div>
                </div>

                <div class="w-full flex mt-2">
                  <div class="w-1/3 pr-3">
                    <label for="activity_id" class="w-full">{{ $t('select_subactivity') }}</label>
                    <b-autocomplete ref="subactivityAutocomplete"
                        v-model="subactivitySearch"
                        :data="filteredSubactivities"
                        :placeholder="$t('select_subactivity')"
                        :custom-formatter="optionLabel"
                        :disabled="!subactivities.length"
                        icon="search"
                        icon-pack="fas"
                        open-on-focus
                        clearable
                        keep-first
                        max-height="350px"
                        @select="onSubactivitySelect"
                        @input="text => !text && onSubactivitySelect(null)"
                    >
                      <template v-slot="props">
                        <span v-if="props.option.planning_code" class="font-bold mr-2">{{ props.option.planning_code }}</span>{{ props.option.name }}
                      </template>
                    </b-autocomplete>
                  </div>

                  <div class="w-1/3 pr-3 flex">
                    <div class="w-1/2 pr-3">
                      <label for="lag" class="w-full">Lag</label>
                      <v-input
                        v-model="item.lag"
                        name="lag"
                        placeholder="Lag"
                        class="w-full"
                        type="number"
                        required
                      />
                    </div>
                    <div class="w-1/2">
                      <label for="lag_type" class="w-full">{{ $t('unit') }}</label>
                      <b-select ref="input"
                        v-model="item.lag_type"
                        :placeholder="$t('select_unit')"
                        name="lag_type"
                        class="w-full"
                        required
                        expanded
                      >
                        <option v-for="option in lagTypes" :value="option.value" :key="option.value">
                          {{ $t(option.text) }}
                        </option>
                      </b-select>
                    </div>
                  </div>

                  <div class="w-1/3 relative">
                    <div v-if="editing !== null" class="w-full absolute bottom-0 flex">
                      <v-button
                        native-type="submit"
                        class="w-1/2 mr-2"
                        :disabled="!item.activity_id || !item.link_type || item.lag === '' || isNaN(item.lag) || validateActivity()"
                      >{{ $t('update') }}
                      </v-button>
                      <v-button color="grey" class="w-1/2" @click="cancelEdit">{{ $t('cancel') }}</v-button>
                    </div>
                    <v-button v-else
                      native-type="submit"
                      class="w-full absolute bottom-0"
                      :disabled="!item.activity_id || !item.link_type || item.lag === '' || isNaN(item.lag) || validateActivity()"
                    >{{ $t('add') }}
                    </v-button>
                  </div>
                </div>

              </form>
            </div>
          </div>
      
      <div class="flex mt-5">
        <div class="w-1/2 p-2">
            <table class="w-full my-2">  
                <tr class="bg-blue-lightest">
                    <th class="border border-blue-lighter has-text-centered p-2 text-blue-dark">{{$t('predecessors')}}</th>
                    <th class="border border-blue-lighter has-text-centered p-2 text-blue-dark">{{$t('type')}}</th>
                    <th class="border border-blue-lighter has-text-centered p-2 text-blue-dark">Lag</th>
                    <th class="border border-blue-lighter has-text-centered p-2 text-blue-dark"></th>
                </tr>
                <tr v-for="(pre, index) in predecessors" :key="'pre_' + pre.activity_id" :class="{ 'bg-blue-lightest': isEditing('predecessors', index) }">
                    <td class="border border-blue-lighter p-2">{{pre.name}}</td>
                    <td class="border border-blue-lighter p-2 has-text-centered">{{pre.link_type}}</td>
                    <td class="border border-blue-lighter p-2 has-text-centered">{{pre.lag}}{{pre.lag_type}}</td>
                    <td class="border border-blue-lighter p-2 has-text-centered whitespace-no-wrap">
                      <i class="fas fa-pencil-alt text-blue cursor-pointer mr-3" :title="$t('edit')" @click="editPrelation('predecessors', index)"></i>
                      <i class="fas fa-trash text-red cursor-pointer" @click="removePrelation('predecessors', index)"></i>
                    </td>
                </tr>
            </table>
        </div>
        <div class="w-1/2 p-2">
            <table class="w-full my-2">  
                <tr class="bg-blue-lightest">
                    <th class="border border-blue-lighter has-text-centered p-2 text-blue-dark">{{$t('successors')}}</th>
                    <th class="border border-blue-lighter has-text-centered p-2 text-blue-dark">{{$t('type')}}</th>
                    <th class="border border-blue-lighter has-text-centered p-2 text-blue-dark">Lag</th>
                    <th class="border border-blue-lighter has-text-centered p-2 text-blue-dark"></th>
                </tr>
                <tr v-for="(suc, index) in successors" :key="'suc_' + suc.activity_id" :class="{ 'bg-blue-lightest': isEditing('successors', index) }">
                    <td class="border border-blue-lighter p-2">{{suc.name}}</td>
                    <td class="border border-blue-lighter p-2 has-text-centered">{{suc.link_type}}</td>
                    <td class="border border-blue-lighter p-2 has-text-centered">{{suc.lag}}{{ suc.lag_type }}</td>
                    <td class="border border-blue-lighter p-2 has-text-centered whitespace-no-wrap">
                      <i class="fas fa-pencil-alt text-blue cursor-pointer mr-3" :title="$t('edit')" @click="editPrelation('successors', index)"></i>
                      <i class="fas fa-trash text-red cursor-pointer" @click="removePrelation('successors', index)"></i>
                    </td>
                </tr>
            </table>
        </div>
      </div>

      <form @submit.prevent="sendForm"
        class="w-full flex sm:flex-row items-center justify-end mb-4 mt-4">
        <v-button type="submit" icon="thumbs-up" icon-pack="far" class="mr-2">{{ $t('save') }}</v-button>
        <v-button color="grey" icon="undo-alt" @click="$router.go(-1)">{{$t('close')}}</v-button>
      </form>
    </div>
  </div>
</template>

<script>
import Vue from "vue"
import axios from 'axios'
import { mapGetters } from 'vuex'

export default {
  middleware: 'auth',
  title: 'linked_activities',
  subtitle: 'linked_activities',
  menuOption: '2-2',

  components: {
  },

  data: () => ({
    activityId: null,
    activity: null,
    predecessors: [],
    successors: [],
    types: ['predecessors', 'successors'],
    linkTypes: ['FC', 'FF', 'CC', 'CF'],
    lagTypes: [
      { value: 'd', text: 'days' },
      { value: 'w', text: 'weeks' },
      { value: 'M', text: 'months' },
      { value: 'y', text: 'years' }
    ],
    activities: [],
    subactivities: [],
    activitySearch: '',
    subactivitySearch: '',
    editing: null, // { type, index } del vínculo que se está editando
    item: {
      type: 'predecessors',
      link_type: 'FC',
      activity_id: null,
      subactivity_id: null,
      lag: "0",
      lag_type: 'd'
    }
  }),

  computed: {
    ...mapGetters({
      project: 'app/project'
    }),

    filteredActivities() {
      return this.filterOptions(this.activities, this.activitySearch)
    },

    filteredSubactivities() {
      return this.filterOptions(this.subactivities, this.subactivitySearch)
    },
  },

  created() {
    this.activityId = this.$route.params.subactivity_id || this.$route.params.activity_id
    this.getActivityData()
    this.getActivities()
  },

  mounted() {

  },

  methods: {


    async getActivityData() {
        const { data } = await axios.get('/api/v2/activity/' + this.activityId + '?with_prelations=1')
        if (data && data.success) {
          this.activity = data.data

          this.successors = data.data.next_activities.map(a => {
            return {
              activity_id: a.id,
              parent_id: a.parent_id,
              name: a.name,
              link_type: a.pivot.link_type,
              lag: a.pivot.lag,
              lag_type: a.pivot.lag_type
            }
          })
          this.predecessors = data.data.pre_activities.map(a => {
            return {
              activity_id: a.id,
              parent_id: a.parent_id,
              name: a.name,
              link_type: a.pivot.link_type,
              lag: a.pivot.lag,
              lag_type: a.pivot.lag_type
            }
          })

        } else {
          this.$notify.error('error_loading_activity_data')
        }
    },

    async getActivities(){
      const { data } = await axios.get("/api/v2/activities", {'params' : { p : this.project.id, per_page: 999}});
      this.activities = data.activities;
    },

    async getSubactivities(activityId){
      const { data } = await axios.get("/api/v2/activity/" + activityId + "/subactivities");
      return data.subactivities;
    },

    async loadSubactivities() {
      this.item.subactivity_id = null;
      this.subactivitySearch = '';
      this.subactivities = this.item.activity_id ? await this.getSubactivities(this.item.activity_id) : [];
    },

    // Precarga en los buscadores la actividad (y subactividad) del vínculo que se edita
    async loadPrelationActivity(prelation) {
      const activityId = prelation.parent_id || prelation.activity_id
      const activity = this.activities.find(a => a.id == activityId)
      this.item.activity_id = activityId
      this.activitySearch = activity ? this.optionLabel(activity) : prelation.name
      this.subactivities = await this.getSubactivities(activityId) || []
      if (prelation.parent_id) {
        const subactivity = this.subactivities.find(a => a.id == prelation.activity_id)
        this.item.subactivity_id = prelation.activity_id
        this.subactivitySearch = subactivity ? this.optionLabel(subactivity) : prelation.name
      }
    },

    optionLabel(option) {
      return option.planning_code ? option.planning_code + ' - ' + option.name : option.name
    },

    // Filtra por código o nombre y ordena por código natural (3.2 antes que 3.10)
    filterOptions(options, search) {
      options = options || []
      const term = (search || '').toLowerCase().trim()
      // Si el texto es la etiqueta de la opción ya seleccionada, mostrar la lista completa
      const selected = options.find(o => this.optionLabel(o).toLowerCase() === term)
      return options
        .filter(o => !term || selected || this.optionLabel(o).toLowerCase().includes(term))
        .sort((a, b) => String(a.planning_code || '').localeCompare(String(b.planning_code || ''), undefined, {
          numeric: true,
          sensitivity: 'base'
        }))
    },

    async onActivitySelect(option) {
      const activityId = option ? option.id : null
      if (activityId === this.item.activity_id) return
      this.item.activity_id = activityId
      await this.loadSubactivities()
      // Si la actividad tiene subactividades, desplegar su lista para que se vean directamente
      if (this.subactivities.length && this.item.activity_id === activityId) {
        this.$nextTick(() => {
          const input = this.$refs.subactivityAutocomplete && this.$refs.subactivityAutocomplete.$el.querySelector('input')
          if (input) input.focus()
        })
      }
    },

    onSubactivitySelect(option) {
      this.item.subactivity_id = option ? option.id : null
    },

    resetItem() {
      this.item.activity_id = null
      this.item.subactivity_id = null
      this.item.lag = "0"
      this.activitySearch = ''
      this.subactivitySearch = ''
      this.subactivities = []
    },

    isEditing(type, index) {
      return this.editing !== null && this.editing.type === type && this.editing.index === index
    },

    editPrelation(type, index) {
      const prelation = this[type][index]
      this.resetItem()
      this.editing = { type, index }
      this.item.type = type
      this.item.link_type = prelation.link_type
      this.item.lag = String(prelation.lag)
      this.item.lag_type = prelation.lag_type
      this.loadPrelationActivity(prelation)
    },

    cancelEdit() {
      this.editing = null
      this.resetItem()
    },

    addPrelation() {
      let findActivity = null;
      if (this.item.subactivity_id) {
        findActivity = this.subactivities.find(a => a.id == this.item.subactivity_id);
      } else {
        findActivity = this.activities.find(a => a.id == this.item.activity_id);
      }
      const prelation = {
        activity_id: findActivity.id,
        parent_id: findActivity.parent_id || null,
        name: findActivity.name,
        link_type: this.item.link_type,
        lag: this.item.lag,
        lag_type: this.item.lag_type
      }

      if (this.editing !== null) {
        // edición: se sustituye el vínculo (puede cambiar de actividad y de predecesora a sucesora)
        if (this.editing.type === this.item.type) {
          this[this.item.type].splice(this.editing.index, 1, prelation)
        } else {
          this[this.editing.type].splice(this.editing.index, 1)
          this[this.item.type].push(prelation)
        }
        this.cancelEdit()
        return
      }

      this[this.item.type].push(prelation)

      this.resetItem()
    },

    // true si la actividad ya está vinculada (sin contar el vínculo que se está editando)
    validateActivity() {
      let activityId = this.item.subactivity_id || this.item.activity_id
      const isOther = (type, index) => !this.isEditing(type, index)
      return this.successors.some((suc, index) => suc.activity_id == activityId && isOther('successors', index))
        || this.predecessors.some((pre, index) => pre.activity_id == activityId && isOther('predecessors', index))
    },

    removePrelation(type, index) {
      if (this.editing !== null && this.editing.type === type) {
        // los índices cambian al borrar: salir de edición para no editar otra fila
        this.cancelEdit()
      }
      this[type].splice(index, 1);
    },

    async sendForm() {
      let body = {
        predecessors: {},
        successors: {}
      }
      this.predecessors.map(pre => {
        body.predecessors[pre.activity_id] = {
          link_type: pre.link_type,
          lag: pre.lag,
          lag_type: pre.lag_type
        }
      })
      this.successors.map(suc => {
        body.successors[suc.activity_id] = {
          link_type: suc.link_type,
          lag: suc.lag,
          lag_type: suc.lag_type
        }
      })

      const { data } = await axios.post('/api/v2/activity/' + this.activityId + '/sync-prelations', body)
      if (data && data.success) {
        this.$notify.success("success");
      }
    },

  }
};
</script>
